import PhoneIcon from '@mui/icons-material/Phone';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { Button, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setSelectedChannel } from '../../../../app/reducers';
import { PortalType } from '../../../../domain/portal';
import { BackoffButton } from '../../../../general/inputs/backoff-button';
import { ProgressButton } from '../../../../general/inputs/progress-button';
import { RootState } from '../../../../store';
import { authorizeWithOtp, requestOtp, resendOtp, resendVerification, verifyOtp } from '../../reducer/session';

const displayInfoByChannel = {
  sms: {
    actionText: 'texted a code to',
    noPhoneActionText: 'texted a code to you',
    resendButtonText: 'Resend Code',
    resendInactiveText: 'Resend',
    alternativeActionIcon: <PhoneIcon />,
    alternativeActionText: 'Call Me Instead',
  },
  call: {
    actionText: 'called you at',
    noPhoneActionText: 'called you',
    resendButtonText: 'Call Me Again',
    resendInactiveText: 'Call',
    alternativeActionIcon: <PhoneIphoneIcon />,
    alternativeActionText: 'Text Me Instead',
  },
};

type Props = {
  loading: boolean;
  nextStepLoading: boolean;
  consentLoading: boolean;
  phone: string;
  portalType: PortalType;
};

const VerifyPhoneForm = ({ loading, nextStepLoading, consentLoading, phone, portalType }: Props) => {
  const dispatch = useDispatch();
  const selectedChannel = useSelector((state: RootState) => state.portal.selectedChannel);
  const channel = selectedChannel ?? 'sms';
  const [otp, setOtp] = useState('');
  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {
      if (portalType === 'apply') {
        dispatch(authorizeWithOtp(otp));
      } else {
        dispatch(verifyOtp(otp));
      }
    },
  });

  const handleOtp = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    if (/^\d*$/.test(input)) {
      setOtp(input.slice(0, 6));
    }
  }, []);

  const {
    actionText,
    noPhoneActionText,
    resendButtonText,
    resendInactiveText,
    alternativeActionIcon,
    alternativeActionText,
  } = displayInfoByChannel[channel];
  const handleResend = useCallback(() => {
    setOtp('');
    if (portalType === 'apply') {
      dispatch(resendVerification());
    } else {
      dispatch(resendOtp(channel));
    }
  }, [channel, dispatch, portalType]);
  const phoneNumberLast4 = phone ? phone.slice(phone.length - 4, phone.length) : '';
  const subtitle = phone
    ? `We just ${actionText} your number ending in ${phoneNumberLast4}.`
    : `We just ${noPhoneActionText}.`;
  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container direction="column">
        <Typography variant="subtitle2" align="center" mb={2} color="textSecondary">
          {subtitle}
        </Typography>
        <TextField
          fullWidth
          value={otp}
          onChange={handleOtp}
          id="otp-input"
          name="otp"
          label="Enter 6 Digit Code"
          autoFocus={true} // eslint-disable-line jsx-a11y/no-autofocus
          data-public
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <BackoffButton
                  onClick={handleResend}
                  size="large"
                  buttonText={resendButtonText}
                  inactiveButtonText={resendInactiveText}
                  disabled={loading}
                />
              </InputAdornment>
            ),
          }}
        />
        <ProgressButton
          type="submit"
          color="primary"
          variant="contained"
          disabled={otp.length !== 6}
          progress={nextStepLoading || consentLoading || loading}
          size="large"
          sx={{ mt: 3 }}
          fullWidth
        >
          Verify Code
        </ProgressButton>
        {portalType === 'verify' && (
          <Button
            variant="text"
            startIcon={alternativeActionIcon}
            fullWidth
            size="large"
            onClick={() => {
              const oppositeChannel = channel === 'sms' ? 'call' : 'sms';
              dispatch(requestOtp({ phone, channel: oppositeChannel }));
              dispatch(setSelectedChannel(oppositeChannel));
            }}
            sx={{ mt: 2 }}
          >
            {alternativeActionText}
          </Button>
        )}
      </Grid>
    </form>
  );
};

export { VerifyPhoneForm };
